import { memo, ReactNode, useCallback, useState } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { ContactPreview } from 'clickNgo/entities/contact/ui'
import { useContactData } from 'clickNgo/features/contact/contactDeactivation/contexts'
import {
  ReassignmentItem,
  ReassignmentItemRow,
} from 'clickNgo/features/contact/contactReassignment'
import { SkipStepWarning } from 'clickNgo/features/contact/contactRelinking/contactRelinkingValidationModal/components/skipStepWarning/SkipStepWarning'
import { useReassignment } from 'clickNgo/features/contact/contactRelinking/contactRelinkingValidationModal/contexts/ReassignmentContext'
import { contactRelinkingModalTranslations } from 'clickNgo/features/contact/contactRelinking/contactRelinkingValidationModal/translation'
import { InfoBlock, InfoBlockRounded, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Nullable } from 'cng-common/models/utils'
import { IForm } from 'core-components/classes/models/IForm'
import { useStaticCrud } from 'core-components/types/helpers'

import { ContactRelinkingFooter } from '../contactRelinkingFooter'
import { ContactRelinkingHeader } from '../contactRelinkingHeader'

import styles from './Reassignment.module.scss'

interface ReassignmentProps {
  title: string
  description: string
  items: ReassignmentItem[]
  onItemChange: (itemKey: string, contact: Nullable<IContact>) => void
  onCancel: () => void
  onProceed: () => void
  warningMsg: ReactNode
  isSkippable?: boolean
  additionalContent?: ReactNode
  isDisabled?: boolean
}

export const Reassignment = memo<ReassignmentProps>(
  ({
    title,
    description,
    items,
    onItemChange,
    onCancel,
    onProceed,
    warningMsg,
    isSkippable,
    additionalContent,
    isDisabled,
  }) => {
    const { contact } = useContactData()
    const { availableContacts } = useReassignment()
    const [previewContact, setPreviewContact] = useState<Nullable<IContact>>(null)
    const [isSkipConfirmationVisible, setIsSkipConfirmationVisible] = useState(false)

    const availableContactsSource = useStaticCrud({
      data: availableContacts.map((contactItem: IContact) => ({
        value: contactItem.contactId,
        label: contactItem.email,
        name: `${contactItem.firstName} ${contactItem.lastName}`,
      })),
      keyField: 'value',
    })

    const isEveryItemHasContact = items.every((item) => !!item.contact)

    const handleSkipConfirmationClose = useCallback(() => {
      setIsSkipConfirmationVisible(false)
    }, [])

    const handleSkipConfirmationProceed = useCallback(() => {
      setIsSkipConfirmationVisible(false)
      onProceed()
    }, [onProceed])

    const handlePreviewClose = useCallback(() => {
      setPreviewContact(null)
    }, [])

    const handleItemChange = useCallback(
      (itemKey: string, value: string) => {
        const selectedContact =
          availableContacts.find((c: IContact) => c.contactId === value) || null
        onItemChange(itemKey, selectedContact)
      },
      [availableContacts, onItemChange]
    )

    const handleProceedClick = useCallback(() => {
      if (isEveryItemHasContact || !availableContacts.length) {
        onProceed()
      } else {
        setIsSkipConfirmationVisible(true)
      }
    }, [availableContacts.length, isEveryItemHasContact, onProceed])

    const renderDropdown = useCallback((item: IForm) => {
      return (
        <span style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem' }}>
          <strong>{item.name}</strong>
          <span>{item.label}</span>
        </span>
      )
    }, [])

    return (
      <div className={styles.container}>
        <ContactRelinkingHeader
          title={title}
          description={description}
          descriptionParams={[{ key: '{email}', value: contact.email }]}
        />
        <InfoBlock
          text={warningMsg}
          type={InfoBlockType.Warning}
          rounded={InfoBlockRounded.Medium}
        />
        <RenderWhen condition={availableContacts.length > 0}>
          <div className={styles.content}>
            {items.map((item) => (
              <ReassignmentItemRow
                key={item.key}
                item={item}
                onItemChange={handleItemChange}
                availableContactsSource={availableContactsSource}
                onPreviewClick={() => setPreviewContact(item.contact!)}
                renderDropdown={renderDropdown}
              />
            ))}
          </div>
        </RenderWhen>
        {additionalContent && <div className={styles.customComponent}>{additionalContent}</div>}
        <ContactRelinkingFooter
          onCancel={onCancel}
          onProceed={handleProceedClick}
          isContinueDisabled={
            isDisabled || (!!availableContacts.length && !isEveryItemHasContact && !isSkippable)
          }
        />
        <ContactPreview
          isVisible={!!previewContact}
          contact={previewContact!}
          onClose={handlePreviewClose}
        />
        <SkipStepWarning
          isVisible={isSkipConfirmationVisible}
          handleSkipConfirmationClose={handleSkipConfirmationClose}
          handleSkipConfirmationProceed={handleSkipConfirmationProceed}
          skipTranslations={{
            title: contactRelinkingModalTranslations.reassignmentFlagsSkipConfirm.title,
            description: contactRelinkingModalTranslations.reassignmentFlagsSkipConfirm.description,
          }}
        />
      </div>
    )
  }
)

Reassignment.displayName = 'ContactRelinkingReassignment'
